import { useLocation } from "wouter";
import { PageMeta } from "@/components/PageMeta";
import {
  Shield, Lock, Eye, Mail, Database, AlertTriangle, Users,
  Activity, HardDrive, Bug, RefreshCw, FileText, ArrowRight,
  CheckCircle2, ChevronRight,
} from "lucide-react";
import { motion } from "framer-motion";

type DocSection = {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  clauseRef?: string;
  items: Array<{ heading: string; body: string }>;
};

const DOCS: DocSection[] = [
  {
    title: "Data Security",
    icon: Lock,
    items: [
      { heading: "Encryption at Rest", body: "All customer data stored in our managed PostgreSQL database is encrypted at rest using AES-256 encryption provided by the underlying cloud provider." },
      { heading: "Encryption in Transit", body: "All API communications use TLS 1.2 or higher. HTTPS is enforced for all endpoints. Internal service-to-service communication uses mutual TLS." },
      { heading: "Database Access", body: "Production database access is limited to application service accounts with least-privilege permissions. No direct developer access to production data without a formal access request and approval." },
    ],
  },
  {
    title: "Access Control",
    icon: Shield,
    items: [
      { heading: "Role-Based Access Control (RBAC)", body: "LaunchTactix enforces a 7-tier RBAC model: owner, admin, manager, sales_rep, member, billing_only, and viewer. Every API endpoint validates the caller's role before granting access." },
      { heading: "Authentication", body: "All user authentication is handled by Clerk, an SOC 2 Type II certified identity provider. We support OAuth 2.0 (Google, GitHub) and email/password with MFA enforcement available at the workspace level." },
      { heading: "Session Management", body: "Sessions are short-lived JWT tokens issued by Clerk. Tokens are validated on every request. Compromised sessions can be revoked immediately." },
    ],
  },
  {
    title: "Audit & Monitoring",
    icon: Eye,
    clauseRef: "ISO 27001 A.8.15 / A.8.16",
    items: [
      { heading: "Immutable Audit Trail", body: "All security-relevant actions (lead management, team changes, settings updates, email opt-outs) are recorded in an append-only audit log with actor identity, timestamp, IP address, and user agent." },
      { heading: "Request Logging", body: "All HTTP requests to the API are logged using structured logging with request ID, method, path, status code, response time, and workspace context." },
      { heading: "Anomaly Monitoring", body: "Health endpoint monitored for uptime detection. Structured error logs reviewed weekly. Alert thresholds trigger on >1% 5xx error rate or repeated authentication failures." },
    ],
  },
  {
    title: "Incident Response",
    icon: AlertTriangle,
    clauseRef: "ISO 27001 A.5.26",
    items: [
      { heading: "Detection & Triage", body: "Security incidents are identified through automated alerts, external vulnerability reports, user-reported issues, or provider notifications. All potential incidents are logged immediately with severity classification (P1–P4)." },
      { heading: "Containment", body: "P1 incidents: immediate action within 1 hour — isolate affected systems, revoke compromised credentials, apply IP blocklist if applicable, notify workspace administrators within 2 hours." },
      { heading: "Notification & Reporting", body: "Data breach affecting personal data: affected workspace administrators are notified within 72 hours of confirmed breach discovery, consistent with GDPR Article 33." },
    ],
  },
  {
    title: "Business Continuity & Backup",
    icon: RefreshCw,
    clauseRef: "ISO 27001 A.5.29 / A.8.13",
    items: [
      { heading: "Backup Policy", body: "The primary PostgreSQL database is backed up automatically by the cloud provider on a daily schedule. Backups are retained for a minimum of 30 days." },
      { heading: "Recovery Objectives", body: "Target RTO: 4 hours. Target RPO: 24 hours. Backup restoration is tested quarterly in a staging environment." },
      { heading: "Continuity Plan", body: "In the event of platform unavailability: status page updated within 15 minutes, workspace administrators notified by email, cloud provider escalation initiated if outage exceeds 30 minutes." },
    ],
  },
  {
    title: "Email Compliance",
    icon: Mail,
    items: [
      { heading: "CAN-SPAM Act", body: "Unsubscribe link management, opt-out processing within 10 business days, and suppression lists to prevent re-sending." },
      { heading: "GDPR Email Requirements", body: "Lawful basis tracking, right-to-erasure via suppression, data retention configuration, and data processing agreement (DPA) tracking." },
      { heading: "CASL / CCPA", body: "Consent tracking (express vs. implied), consent timestamps, and consent source recording per lead. CCPA opt-out support and suppression." },
    ],
  },
  {
    title: "Vulnerability Management",
    icon: Bug,
    clauseRef: "ISO 27001 A.8.8",
    items: [
      { heading: "Dependency Scanning", body: "Dependency auditing runs on every code change to identify known vulnerabilities. High and critical severity findings block merges and trigger immediate remediation tasks." },
      { heading: "Security Headers", body: "All HTTP responses include Helmet-managed security headers: Content-Security-Policy, X-Frame-Options (DENY), HSTS, X-Content-Type-Options, and Referrer-Policy." },
      { heading: "Review Cadence", body: "Quarterly vulnerability review covering all dependencies, provider security advisories, and CVEs. Critical findings remediated within 72 hours, all others within 30 days." },
    ],
  },
  {
    title: "HR Security",
    icon: Users,
    clauseRef: "ISO 27001 A.6.1 / A.6.2 / A.6.4",
    items: [
      { heading: "Pre-Employment Screening", body: "Team members with administrative platform access complete identity verification and professional reference checks before access is granted." },
      { heading: "Security Responsibilities", body: "All team members with platform access are responsible for maintaining credential confidentiality, reporting suspected incidents, and using strong unique passwords with MFA." },
      { heading: "Offboarding", body: "When a team member's access is terminated, their workspace role is removed immediately, Clerk invalidates all active sessions automatically, and the event is recorded in the audit log." },
    ],
  },
];

const TRUST_BADGES = [
  { label: "SOC 2 Aligned", color: "border-green-500/30 text-green-400" },
  { label: "ISO 27001 Aligned", color: "border-blue-500/30 text-blue-400" },
  { label: "GDPR Ready", color: "border-violet-500/30 text-violet-400" },
  { label: "CCPA Ready", color: "border-violet-500/30 text-violet-400" },
  { label: "CAN-SPAM Ready", color: "border-amber-500/30 text-amber-400" },
  { label: "CASL Ready", color: "border-amber-500/30 text-amber-400" },
  { label: "TLS 1.2+ Enforced", color: "border-white/20 text-white/50" },
  { label: "AES-256 Encryption", color: "border-white/20 text-white/50" },
  { label: "Multi-Tenant Isolated", color: "border-white/20 text-white/50" },
];

export function PublicSecurity() {
  const [, setLocation] = useLocation();

  return (
    <div className="min-h-screen bg-[#030305] text-foreground selection:bg-primary/30">
      <PageMeta
        title="Security & Trust — LaunchTactix"
        description="LaunchTactix platform security: TLS 1.2+, AES-256 encryption, SOC 2 Type II readiness, 7-tier RBAC, immutable audit trails, and incident response."
        canonical="https://launchtactix.com/security"
      />
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 py-4 flex justify-between items-center backdrop-blur-md bg-[#030305]/80 border-b border-white/5">
        <button onClick={() => setLocation("/")} className="flex items-center">
          <img src={`${import.meta.env.BASE_URL}images/logo-wordmark.png`} alt="LaunchTactix" width={606} height={216} className="h-9 w-auto mix-blend-screen" />
        </button>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setLocation("/")}
            className="text-xs text-white/40 hover:text-white font-mono tracking-widest uppercase transition-colors hidden sm:block"
          >
            ← Home
          </button>
          <button
            onClick={() => setLocation("/sign-in")}
            className="text-xs font-bold tracking-widest text-white/70 hover:text-white transition-colors uppercase px-4 py-2 border border-white/10 hover:bg-white/5 rounded-sm"
          >
            Sign In
          </button>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-28 pb-24">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-16 sm:mb-20"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/10 text-[10px] font-bold text-primary mb-6 tracking-[0.2em] uppercase">
            <Shield className="w-3 h-3" />
            Security & Trust
          </div>
          <h1 className="text-4xl sm:text-6xl md:text-8xl font-display font-bold tracking-tighter text-white mb-6 uppercase leading-[0.9]">
            Built To<br /><span className="text-white/30">Protect.</span>
          </h1>
          <p className="text-base sm:text-xl text-white/50 max-w-2xl font-light leading-relaxed">
            LaunchTactix is engineered from the ground up with enterprise security standards — SOC 2 aligned, ISO 27001 ready, and designed to support compliance with GDPR, CCPA, CASL, and CAN-SPAM.
          </p>
        </motion.div>

        {/* Trust badges */}
        <div className="flex flex-wrap gap-2 mb-16">
          {TRUST_BADGES.map(b => (
            <span key={b.label} className={`inline-flex items-center gap-1.5 px-3 py-1.5 border rounded-sm text-xs font-mono tracking-widest uppercase ${b.color}`}>
              <CheckCircle2 className="w-3 h-3 shrink-0" />
              {b.label}
            </span>
          ))}
        </div>

        {/* Compliance highlights */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/5 mb-20">
          {[
            { icon: Shield, title: "SOC 2 Readiness", desc: "In-platform SOC 2 readiness dashboard with per-control evidence tracking, live readiness score, and exportable CSV report." },
            { icon: Lock, title: "ISO 27001 Readiness", desc: "Full Annex A control checklist (A.5–A.8) with implemented/configured/in-progress status, scoring, and domain-level progress tracking." },
            { icon: Database, title: "Asset, Vendor & Risk Registers", desc: "Database-backed registers for information assets, third-party vendors, and risk assessments — driving real-time control status updates." },
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * i, duration: 0.6 }}
              className="bg-[#0a0a12] p-6 sm:p-10"
            >
              <item.icon className="w-8 h-8 text-primary mb-6" />
              <h3 className="text-lg font-display font-bold text-white mb-3 uppercase tracking-wide">{item.title}</h3>
              <p className="text-sm text-white/40 leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Detailed sections */}
        <div className="space-y-12">
          {DOCS.map((section, si) => {
            const Icon = section.icon;
            return (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.05 * si }}
              >
                <div className="flex items-center gap-3 mb-5 pb-4 border-b border-white/5">
                  <Icon className="w-5 h-5 text-primary" />
                  <div>
                    <h2 className="text-lg font-display font-bold text-white uppercase tracking-wide">{section.title}</h2>
                    {section.clauseRef && (
                      <p className="text-[10px] font-mono text-white/30 tracking-widest uppercase">{section.clauseRef}</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {section.items.map(item => (
                    <div key={item.heading} className="bg-white/[0.02] border border-white/5 p-5">
                      <h3 className="text-sm font-semibold text-white mb-2">{item.heading}</h3>
                      <p className="text-xs text-white/40 leading-relaxed">{item.body}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Vulnerability disclosure */}
        <div className="mt-16 p-6 sm:p-10 border border-primary/20 bg-primary/5">
          <div className="flex items-start gap-4">
            <Bug className="w-6 h-6 text-primary shrink-0 mt-1" />
            <div>
              <h3 className="text-base font-display font-bold text-white mb-2 uppercase tracking-wide">Responsible Disclosure</h3>
              <p className="text-sm text-white/50 leading-relaxed">
                If you discover a security vulnerability in LaunchTactix, please report it to our security team. We aim to acknowledge reports within 24 hours and resolve critical issues within 72 hours. We appreciate responsible disclosure and will not pursue legal action against researchers who act in good faith.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <button
            onClick={() => setLocation("/sign-in")}
            className="inline-flex items-center gap-3 px-8 py-5 bg-white text-black font-display font-bold tracking-widest uppercase text-sm hover:bg-primary hover:text-white transition-all duration-300 group"
          >
            Launch Engine <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
          <p className="mt-4 text-xs text-white/20 font-mono">Full platform access · No credit card required on Scout plan</p>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-10 px-6 border-t border-white/5 bg-black flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="flex items-center opacity-60">
          <img src={`${import.meta.env.BASE_URL}images/logo-wordmark.png`} alt="LaunchTactix" width={606} height={216} className="h-7 w-auto mix-blend-screen" />
        </div>
        <div className="flex items-center gap-6 text-xs font-mono text-white/20 uppercase tracking-widest">
          <button onClick={() => setLocation("/")} className="hover:text-white/50 transition-colors">Home</button>
          <button onClick={() => setLocation("/privacy")} className="hover:text-white/50 transition-colors">Privacy</button>
          <span>{new Date().getFullYear()}</span>
        </div>
      </footer>
    </div>
  );
}
